import classNames from "classnames"
import * as math from "mathjs"
import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { CSSTransition } from "react-transition-group"

import Button from "components/button"
import ButtonLoader from "components/button-loader"
import CheckPermission from "components/check-permission/CheckPermission"
import Input from "components/input"
import Icon from "icons"
import { toast } from "react-toastify"
import request from "request"
import prettierNumber from "utils/prettier-number"
import Modal from "./components/create-modal"
import modalStyles from "./components/create-modal.module.scss"
import home from "./home"
import styles from "./home-group-loans.module.scss"

const firstDayOfYear = new Date(new Date().getFullYear(), 0, 1)
const lastDayOfYear = new Date(new Date().getFullYear(), 11, 31)

const HomeGroupLoans = () => {
  const [from, setFrom] = useState<Date>(firstDayOfYear)
  const [to, setTo] = useState<Date>(lastDayOfYear)
  const [page, setPage] = useState(1)
  const [rowCount, setRowCount] = useState(1)
  const [newApplicationModal, setNewApplicationModal] = useState(false)
  const [pending, setPending] = useState(true)
  const [xlsxPending, setXlsxPending] = useState(false)
  const [search, setSearch] = useState("")
  const [data, setData] = useState<home.result[]>([])

  const navigate = useNavigate()

  const fetchApplications = useCallback(
    async (fd: string, td: string) => {
      setPending(true)
      document.querySelector("#applicationsHistory")?.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      })
      try {
        const res = await request.get(
          `/applications/group/history?fd=${fd}&td=${td}&page=${page}`,
        )
        const d = res.data
        setData(d.records)
        setRowCount(d.total_count)
      } catch (error: any) {
        const errorMessage =
          (error as any)?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error fetching applications: ${errorMessage}`)
      } finally {
        setPending(false)
      }
    },
    [page],
  )

  const handleFilter = useCallback(() => {
    const fd = from.toLocaleDateString("en-GB").split("/").reverse().join("-")
    const td = to.toLocaleDateString("en-GB").split("/").reverse().join("-")
    fetchApplications(fd, td)
  }, [from, to, fetchApplications])

  const handleClear = useCallback(() => {
    setFrom(firstDayOfYear)
    setTo(lastDayOfYear)
    setPage(1)
    const fd = firstDayOfYear
      .toLocaleDateString("en-GB")
      .split("/")
      .reverse()
      .join("-")
    const td = lastDayOfYear
      .toLocaleDateString("en-GB")
      .split("/")
      .reverse()
      .join("-")
    fetchApplications(fd, td)
  }, [handleFilter])

  const handleExcel = async () => {
    const fd = from.toLocaleDateString("en-GB").split("/").reverse().join("-")
    const td = to.toLocaleDateString("en-GB").split("/").reverse().join("-")

    setXlsxPending(true)

    request
      .get(`/audit/report`, {
        params: {
          fd,
          td,
          rep_type: "xlsx",
          source_type: "group_application",
        },
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `group_application_report_xlsx_${fd}_to_${td}.xlsx`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        toast.success("Excel file downloaded successfully")
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error downloading Excel file: ${errorMessage}`)
      })
      .finally(() => setXlsxPending(false))
  }

  useEffect(() => {
    const fd = from.toLocaleDateString("en-GB").split("/").reverse().join("-")
    const td = to.toLocaleDateString("en-GB").split("/").reverse().join("-")
    fetchApplications(fd, td)
  }, [page, fetchApplications])

  return (
    <>
      <div className={styles.tools}>
        <div className={styles.filterBox}>
          <div className={styles.input}>
            <Input title="From" value={from} type="date" onSetDate={setFrom} />
          </div>
          <div className={styles.input}>
            <Input title="To" value={to} type="date" onSetDate={setTo} />
          </div>
          <div className={styles.buttonGroup}>
            <Button pending={pending} onClick={handleFilter}>
              Filter
            </Button>
            <Button pending={pending} color="red" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <form
            className={styles.findApplication}
            onSubmit={(e) => {
              e.preventDefault()
              if (search.trim() === "") return
              navigate(`/application-group/${search}`, { replace: true })
            }}
          >
            <Input
              type="integer"
              value={search}
              onInput={setSearch}
              placeholder="Group ID"
            />
            <Button
              type="submit"
              blocked={search.trim() === ""}
              color={search.trim() === "" ? "grey" : "orange"}
            >
              Go to group application
            </Button>
          </form>
          <CheckPermission permissions={["export_report"]}>
            <Button pending={xlsxPending} color="orange" onClick={handleExcel}>
              Export .XLSX file
            </Button>
          </CheckPermission>
          <CheckPermission permissions={["score"]}>
            <Button color="green" onClick={() => setNewApplicationModal(true)}>
              New group application
            </Button>
          </CheckPermission>
        </div>
      </div>
      {pending && (
        <div className={styles.tableLoading}>
          {Array.from({ length: 15 }, (_, index) => (
            <div className={styles.tableLoadingRow} key={index}>
              <ButtonLoader />
            </div>
          ))}
        </div>
      )}
      {!pending && data?.length !== 0 && (
        <div className={styles.table}>
          {data?.map((item) => (
            <Link
              to={`/application-group/${item.id}`}
              className={styles.tableRowWrap}
              key={item.id}
            >
              <div className={styles.tableRow}>
                <div className={styles.item}>
                  <span>Group ID</span> {item.id}
                </div>
                <div className={styles.item}>
                  <span>Expert</span>
                  {item?.applications[0]?.created_by?.email}
                </div>
                <div className={styles.item}>
                  <span>Create date</span>
                  {new Date(item.created_at).toLocaleString()}
                </div>
                <div className={styles.item}>
                  <span>Amount</span>
                  {prettierNumber(item.applications[0]?.loanAmount)} JOD
                </div>
                <div className={styles.item}>
                  <span>Duration</span>
                  {item.applications[0]?.scoring_log?.loanDuration} mon.
                </div>
                <div className={styles.item}>
                  <span>Interest rate</span>
                  {item.applications[0]?.interestRate?.toFixed(2)}%
                </div>
                <div
                  className={classNames(
                    styles.item,
                    item.is_approved_group === "approved" && styles.green,
                    item.is_approved_group === "rejected" && styles.red,
                    item.is_approved_group === "review" && styles.yellow,
                  )}
                >
                  <span>Result</span>
                  {item.is_approved_group === "approved"
                    ? "Approved"
                    : item.is_approved_group === "rejected"
                      ? "Rejected"
                      : "Draft"}
                </div>
                <div className={styles.item}>
                  <span>Probability of approval</span>
                  {item.applications[0]?.scoring_log?.prediction
                    ? `${item.applications[0]?.scoring_log?.prediction}%`
                    : "–"}
                </div>
                <div className={styles.item}>
                  <span>Threshold</span>
                  {item.applications[0]?.scoring_log?.threshold
                    ? `${item.applications[0]?.scoring_log?.threshold * 100}%`
                    : "–"}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {!pending && data?.length === 0 && (
        <div className={styles.tableIsEmptyInfo}>No applications</div>
      )}
      {rowCount > 15 && (
        <div className={styles.pagination}>
          <button
            className={classNames(styles.arrow, page <= 1 && styles.disabled)}
            onClick={() => setPage(math.max(page - 1, 1))}
          >
            <Icon name="arrowLeft" />
          </button>
          <div className={styles.label}>
            {page} / {math.ceil(rowCount / 15)}
          </div>
          <button
            className={classNames(
              styles.arrow,
              page * 15 >= rowCount && styles.disabled,
            )}
            onClick={() =>
              setPage(math.min(page + 1, math.ceil(rowCount / 15)))
            }
          >
            <Icon name="arrowRight" />
          </button>
        </div>
      )}
      <CSSTransition
        in={!!newApplicationModal}
        timeout={150}
        unmountOnExit
        classNames={{
          enter: modalStyles.modalEnter,
          enterActive: modalStyles.modalEnterActive,
          exit: modalStyles.modalExit,
          exitActive: modalStyles.modalExitActive,
        }}
      >
        <Modal onClose={() => setNewApplicationModal(false)} />
      </CSSTransition>
    </>
  )
}

export default HomeGroupLoans
